import React, { useCallback, useMemo, useState } from "react";
import { useCartContext } from "../../cart";
import { DealType } from "../domain/DealSelection.model";
import SyncAltTwoToneIcon from "@mui/icons-material/SyncAltTwoTone";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { GtmService } from "../../infrastructure/analytics/GtmService";
import { addPendingOrder } from "../infrastructure/pendingOrderApi";
import { isSuccess } from "../../common/domain/RemoteData.model";
import { useComercialContext } from "../../comercial/ComercialContext";

const isSelectionInvalid = (productSelection) => {
    const { price, quantity, availability, dealType } = productSelection;
    return (
        price?.sellingPrice <= 0 ||
        quantity <= 0 ||
        (dealType === DealType.LOT && availability.isAvailable === false)
    );
};


const handleAddToCart = async ({ setLoading, addProduct, afterAddToCart, productSelection, product }) => {
    try {
        setLoading(true);
        await addProduct(productSelection);
        GtmService.logAddToCart(product, productSelection.quantity, productSelection.price);
        toast.success(`Producto agregado al carrito exitosamente.`)
    } catch (error) {
        toast.error(`No pudo agregarse el producto al carrito. Por favor, reintente más tarde o contacte con nosotros.`)
    } finally {
        afterAddToCart();
        setLoading(false);
    }
};

/**
 * La cantidad pedida supera el stock: agrega al carrito solo las unidades
 * disponibles (si hay) y guarda el exceso como pedido pendiente.
 *
 * A diferencia del split del checkout (AddToPendingButton), aquí controlamos
 * el alta, así que basta con recortar la selección a availableUnits antes de
 * llamar a addProduct — no hace falta PUT cart/product/:id/quantity.
 */
const handleSplitAddToCart = async ({
    setLoading,
    addProduct,
    afterAddToCart,
    productSelection,
    product,
    availableUnits,
    pendingUnits,
    actingAsNumCliente
}) => {
    setLoading(true);
    let addedToCart = 0;
    try {
        if (availableUnits > 0) {
            await addProduct({ ...productSelection, quantity: availableUnits });
            GtmService.logAddToCart(product, availableUnits, productSelection.price);
            addedToCart = availableUnits;
        }
        const result = await addPendingOrder(product.code, pendingUnits, actingAsNumCliente);
        if (!isSuccess(result)) {
            throw new Error('addPendingOrder failed');
        }
        if (addedToCart > 0) {
            toast.success(`Se agregaron ${addedToCart} unidades al carrito; las otras ${pendingUnits} quedaron en pendientes.`);
        } else {
            toast.success(`Las ${pendingUnits} unidades quedaron en pendientes.`);
        }
    } catch (error) {
        console.error('Failed to split add to cart/pending:', error);
        if (addedToCart > 0) {
            toast.error(`Se agregaron ${addedToCart} unidades al carrito, pero no pudimos dejar las otras ${pendingUnits} en pendientes. Por favor, reintente más tarde.`);
        } else {
            toast.error(`No pudo guardarse el producto en tus pedidos pendientes. Por favor, reintente más tarde o contacte con nosotros.`);
        }
    } finally {
        afterAddToCart();
        setLoading(false);
    }
};

/**
 * Confirmación previa al split: el botón de compra se queda siempre como
 * "Agregar" y, si la cantidad pedida supera el stock, al pulsarlo salta este
 * diálogo explicando el reparto (decisión de equipo: un botón cuyo texto
 * cambia con la cantidad quedaba raro, y en la validación final era tarde).
 */
const ConfirmSplitModal = ({ show, onCancel, onConfirm, productName, requestedUnits, availableUnits, pendingUnits, loading }) => (
    <Modal show={show} onHide={onCancel} centered>
        <Modal.Header closeButton>
            <Modal.Title className="h5">No hay stock suficiente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {availableUnits > 0 ? (
                <>
                    <p>
                        Has pedido <strong>{requestedUnits}</strong> unidades de <strong>{productName}</strong> y
                        ahora mismo solo tenemos <strong>{availableUnits}</strong>.
                    </p>
                    <p className="mb-0">
                        Podemos agregar las <strong>{availableUnits}</strong> disponibles a tu carrito y guardar
                        las <strong>{pendingUnits}</strong> restantes como pedido pendiente.
                    </p>
                </>
            ) : (
                <p className="mb-0">
                    Ahora mismo no tenemos stock de <strong>{productName}</strong>. Podemos guardar
                    las <strong>{pendingUnits}</strong> unidades como pedido pendiente.
                </p>
            )}
        </Modal.Body>
        <Modal.Footer>
            <button type="button" className="btn btn-dark" onClick={onCancel} disabled={loading}>
                Cancelar
            </button>
            <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={loading}>
                <div className="d-flex gap-1 align-items-center">
                    {availableUnits > 0
                        ? <span>Agregar {availableUnits} y dejar {pendingUnits} en pendientes</span>
                        : <span>Añadir {pendingUnits} a pendientes</span>}
                    {loading && <Spinner size="sm" />}
                </div>
            </button>
        </Modal.Footer>
    </Modal>
);

/**
 * Botón de compra de la ficha y de la tarjeta plana.
 *
 * Si la cantidad seleccionada supera el stock (product.availableUnits), al
 * pulsar "Agregar" salta una confirmación que explica el reparto y hace el
 * split: lo disponible al carrito y el exceso a pendientes. Aplica igual con
 * product.onDemand: el backend valida stock para todos.
 *
 * @param productSelection
 * @returns {Element}
 * @constructor
 */
export const AddToCartButton = ({ productSelection, hasStock, afterAddToCart = () => null, product }) => {
    const { isOnCart, addProduct } = useCartContext();
    const { actingAsCliente } = useComercialContext();
    const [loading, setLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const invalidSelection = useMemo(() => isSelectionInvalid(productSelection), [productSelection]);
    const shouldAdd = useMemo(() => isOnCart(productSelection?.id), [isOnCart, productSelection?.id]);

    const availableUnits = Math.max(product?.availableUnits ?? 0, 0);
    const requestedUnits = productSelection.quantity;
    // Bypass de stock (bajo pedido / a medida): se agrega siempre entero al carrito,
    // sin split a pendientes — el backend tampoco lo bloquea por stock.
    const stockBypass = product?.stockBypass === true;
    const exceedsStock = !stockBypass && requestedUnits > availableUnits;
    const pendingUnits = exceedsStock ? requestedUnits - availableUnits : 0;

    // Sin stock ya no se deshabilita: siempre hay acción posible (pendientes).
    const disabled = useMemo(() => invalidSelection || loading, [invalidSelection, loading]);

    const addToCart = useCallback(async () => {
        if (exceedsStock) {
            setShowConfirm(true);
            return;
        }
        await handleAddToCart({ setLoading, addProduct, afterAddToCart, productSelection, product });
    }, [setLoading, addProduct, afterAddToCart, productSelection, product, exceedsStock]);

    const confirmSplit = useCallback(async () => {
        await handleSplitAddToCart({
            setLoading,
            addProduct,
            afterAddToCart,
            productSelection,
            product,
            availableUnits,
            pendingUnits,
            actingAsNumCliente: actingAsCliente?.numCliente
        });
        setShowConfirm(false);
    }, [setLoading, addProduct, afterAddToCart, productSelection, product, availableUnits, pendingUnits, actingAsCliente?.numCliente]);

    return (
        <>
            <button type="button" className="btn btn-primary" disabled={disabled} onClick={addToCart}>
                <div className="d-flex gap-1 align-items-center">
                    {
                        shouldAdd === true ? (
                            <>
                                <SyncAltTwoToneIcon />&nbsp;<span>Actualizar</span>
                            </>
                        ) : (
                            <>
                                <ShoppingCartCheckoutIcon />&nbsp;<span>Agregar</span>
                            </>
                        )
                    }
                    {loading && !showConfirm && <Spinner size="sm" />}
                </div>
            </button>
            <ConfirmSplitModal
                show={showConfirm}
                onCancel={() => !loading && setShowConfirm(false)}
                onConfirm={confirmSplit}
                productName={product?.name}
                requestedUnits={requestedUnits}
                availableUnits={availableUnits}
                pendingUnits={pendingUnits}
                loading={loading}
            />
        </>
    );
}
